import classNames from 'classnames';
import React, { ClipboardEvent, useCallback, useEffect, useState } from 'react';

import { PlayersBatchProcessing } from '@/components/PlayersSearch/usePlayersByIds';
import { InlineIconButton } from '../../InlineIconButton';
import { InlineSpinner } from '../../InlineSpinner';
import UploadAndParseField from './UploadAndParseField';

type Props = {
  list: (items: object[] | undefined) => void;
  config: ConfigBulkProps;
};

type OnImportBulkProps = {
  (items: string[] | undefined): {
    results: any[] | undefined;
    loading: boolean;
  };
};

type ItemProps = {
  itemId: string;
  name: string;
};

export type ConfigBulkProps = {
  listItem: (item: ListItemProps<any>) => ItemProps;
  enableBulk: true;
  onImportBulk: OnImportBulkProps;
  batchProcessing?: PlayersBatchProcessing;
  defaultAttrName: string;
};

export type ListItemProps<T> = T & object;

const BulkImportField = ({ list, config }: Props) => {
  const [options, setOptions] = useState<string[]>([]);
  const [pasted, setPasted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [results, setResults] = useState<any[] | undefined>(undefined);
  const { batchProcessing, onImportBulk, listItem, defaultAttrName } = config;

  const noOptions = options.length === 0 || options[0] === '';
  const disableTextArea = disabled || loading || batchProcessing?.isProcessing;
  const notEverythingImported = options.length > 0 && disabled;

  // Process and clean the input
  const processInputData = useCallback((data: string): string[] => {
    const processedData = data.split(/,| /).join('\n');
    return processedData.split('\n').filter((item) => item.trim() !== '');
  }, []);

  useEffect(() => {
    if (options.length > 0 && options[0] !== '') {
      const importResult = onImportBulk(options);
      setResults(importResult.results);
    } else {
      setResults(undefined);
    }
  }, [options, onImportBulk]);

  // Update results when batch processing completes
  useEffect(() => {
    if (
      batchProcessing?.results &&
      batchProcessing.results.length > 0 &&
      options.length > 0
    ) {
      setResults(batchProcessing.results as any[]);
    }
  }, [batchProcessing?.results, options.length]);

  useEffect(() => {
    setDisabled(false);
  }, [options]);

  const onSuccessImport = useCallback(() => {
    const itemsToRemove = results?.map((result) => result[defaultAttrName]);
    if (itemsToRemove) {
      setOptions((prevOptions) =>
        prevOptions.filter((item) => !itemsToRemove.includes(item)),
      );
    }
  }, [results, defaultAttrName]);

  const onChangeTextarea = useCallback(
    (value: string) => {
      setPasted(false);
      const newOptions = processInputData(value);
      setOptions(newOptions);
    },
    [processInputData],
  );

  const onPaste = useCallback(
    (e: ClipboardEvent) => {
      e.preventDefault();
      setPasted(true);

      const data = e.clipboardData.getData('Text');
      const newOptions = processInputData(data);

      setOptions((prevOptions) => {
        const existingItems = prevOptions.filter((item) => item.trim() !== '');

        return existingItems.length > 0
          ? [...existingItems, ...newOptions]
          : newOptions;
      });

      setPasted(false);
    },
    [processInputData],
  );

  const getUploadedList = useCallback((items: string[]) => {
    if (items.length > 0) {
      setOptions((prevOptions) => prevOptions.concat(items));
    }
  }, []);

  const getBulkList = useCallback(
    (items: object[] | undefined) => items?.map(listItem),
    [listItem],
  );

  const onClickImportList = useCallback(() => {
    setLoading(true);
    const bulkList = getBulkList(results);
    list(bulkList);
    setDisabled(true);
    onSuccessImport();
    setLoading(false);
  }, [results, getBulkList, list, onSuccessImport]);

  const onClickClearList = useCallback(() => {
    setDisabled(false);
    setOptions([]);
  }, []);

  const renderImportButton = () => {
    if (noOptions) {
      return (
        <UploadAndParseField
          list={getUploadedList}
          disabled={batchProcessing?.isProcessing}
        />
      );
    }
    return (
      <>
        {notEverythingImported ? (
          <InlineIconButton
            type="button"
            onClick={onClickClearList}
            disabled={batchProcessing?.isProcessing}
          >
            Clear list
          </InlineIconButton>
        ) : (
          <InlineIconButton
            type="button"
            onClick={onClickImportList}
            disabled={batchProcessing?.isProcessing}
          >
            Add to the list
          </InlineIconButton>
        )}
        <div className="ml-auto flex items-center">
          {options.length} {options.length === 1 ? 'item' : 'items'}{' '}
          {notEverythingImported && 'left'}
        </div>
      </>
    );
  };

  const progressPercentage = Math.round(batchProcessing?.progress || 0);

  return (
    <div className="w-full mt-1">
      <textarea
        disabled={disableTextArea}
        className={classNames(
          `w-full h-24 border-gray-300 text-xs rounded-t-md align-top`,
          {
            'bg-gray-200 text-red-600': disableTextArea,
          },
        )}
        placeholder="Copy and paste your list of items here, separated by comma, space or new line or click upload a CSV file"
        value={
          pasted
            ? ''
            : options.length > 1000
              ? `${options.slice(0, 1000).join('\n')}\n... and ${options.length - 1000} more items`
              : options.join('\n')
        }
        onChange={(e) => onChangeTextarea(e.target.value)}
        onPaste={(e) => onPaste(e)}
      >
        <InlineSpinner />
      </textarea>
      {batchProcessing?.errors && batchProcessing.errors.length > 0 && (
        <div className="px-3 py-2 bg-red-50 border-l border-r border-gray-300">
          <div className="text-xs text-red-600">
            <div className="font-medium mb-1">Processing Errors:</div>
            {batchProcessing.errors.map((error, index) => (
              <div key={index} className="mb-1">
                • {error}
              </div>
            ))}
          </div>
        </div>
      )}
      <div
        className={classNames(
          'relative flex p-1 pl-0 items-center rounded-b border-gray-300 h-9 border text-sm text-gray-500 border-t-0 overflow-hidden',
        )}
        style={
          batchProcessing?.isProcessing
            ? {
                background: `linear-gradient(to right, rgba(59, 130, 246, 0.1) ${progressPercentage}%, transparent ${progressPercentage}%)`,
              }
            : undefined
        }
      >
        {batchProcessing?.isProcessing ? (
          <span className="pl-2">
            Processing {batchProcessing.processedItems} /{' '}
            {batchProcessing.totalItems} players...
          </span>
        ) : (
          renderImportButton()
        )}
      </div>
    </div>
  );
};

export default BulkImportField;
